import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AdminAuthProvider } from "@/lib/admin_auth";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline";
import Today from "@/pages/Today";
import Import from "@/pages/Import";
import AuthCallback from "@/pages/AuthCallback";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Catalog from "@/pages/Catalog";
import VendorDetail from "@/pages/VendorDetail";
import EditorDetail from "@/pages/EditorDetail";
import Delivery from "@/pages/Delivery";
import DeliveryDetail from "@/pages/DeliveryDetail";
import MyWork from "@/pages/MyWork";
import Accounts from "@/pages/Accounts";
import Settings from "@/pages/Settings";
import Trash from "@/pages/Trash";
import AuditLog from "@/pages/AuditLog";
import Help from "@/pages/Help";
import AdminLogin from "@/pages/AdminLogin";
import AdminShell from "@/pages/AdminShell";
import AdminMfaScreen from "@/pages/AdminMfaScreen";
import { useAdminAuth } from "@/lib/admin_auth";


/** Module 6D Phase 7 — When the admin auth provider holds an MFA
 *  challenge (post-OAuth, pre-TOTP), render the MFA screen instead of
 *  any other admin children. The challenge state lives ONLY in
 *  component state — never persisted. Once `completeMfa` fires, the
 *  full admin token is stored and children render normally. */
function AdminMfaGate({ children }) {
  const { mfaChallenge } = useAdminAuth();
  if (mfaChallenge) return <AdminMfaScreen />;
  return children;
}
import Signup from "@/pages/Signup";
import ImpersonationLaunch from "@/pages/ImpersonationLaunch";
import UpgradeModal from "@/components/UpgradeModal";
import { registerUpgradeInterceptor } from "@/lib/billing";

// Module 6C.4 Layer 1 — Single registration of the 402 → upgrade-prompt
// interceptor. Runs once at module load (before App mounts) so every API
// call from then on routes structured 402 detail to the modal.
registerUpgradeInterceptor();

function LoadingScreen() {
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center"
      style={{ backgroundColor: "var(--brand-paper)" }}
      data-testid="loading-screen"
    >
      <div className="kicker">LOADING</div>
    </div>
  );
}

function RootRoute() {
  const { user, loading, isEditor } = useAuth();
  // Post-domain-split (app.snapopsai.com is CRM-only; snapopsai.com serves
  // the marketing site): "/" on the app subdomain no longer needs to show
  // the embedded marketing landing. Unauthenticated visitors get bounced
  // to /login. The static #static-landing div in public/index.html is
  // hidden here so it never bleeds through during the bounce. (Full
  // removal of that block is tracked as a separate cleanup task.)
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const sl = document.getElementById("static-landing");
    const rt = document.getElementById("root");
    if (sl) sl.style.display = "none";
    if (rt) rt.style.display = "";
  }, []);

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={isEditor ? "/my-work" : "/dashboard"} replace />;
}

function LoginRoute() {
  const { user, loading, isEditor } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={isEditor ? "/my-work" : "/dashboard"} replace />;
  return <Login />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// Phase 5 Slice B — gate owner pages from editor users at the SPA layer
// (server already returns 403, but we redirect cleanly to /my-work to avoid
// flashes of empty owner pages).
function OwnerRoute({ children }) {
  const { user, loading, isEditor } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (isEditor) return <Navigate to="/my-work" replace />;
  return children;
}

function EditorRoute({ children }) {
  const { user, loading, isEditor } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (!isEditor) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith("/admin");
  // Synchronous fragment check — process session_id BEFORE any protected
  // route renders. EXCEPT on /admin paths: there the AdminAuthProvider's
  // own checkAuth() handles the OAuth exchange against the admin mint
  // endpoint, so we must NOT short-circuit to the studio AuthCallback.
  if (location.hash?.includes("session_id=") && !isAdminPath) {
    return <AuthCallback />;
  }
  // Admin routes mount under their own provider (separate auth context,
  // separate localStorage key, separate Bearer scheme). A studio session
  // in `ls.auth.token` is structurally invisible here.
  if (isAdminPath) {
    return (
      <AdminAuthProvider>
        <AdminMfaGate>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={<AdminShell />} />
          </Routes>
        </AdminMfaGate>
      </AdminAuthProvider>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/impersonate/launch" element={<ImpersonationLaunch />} />
      <Route path="/my-work" element={<EditorRoute><MyWork /></EditorRoute>} />
      <Route path="/dashboard" element={<OwnerRoute><Dashboard /></OwnerRoute>} />
      <Route path="/leads" element={<OwnerRoute><Pipeline /></OwnerRoute>} />
      <Route path="/projects" element={<OwnerRoute><Projects /></OwnerRoute>} />
      <Route path="/projects/:projectId" element={<OwnerRoute><ProjectDetail /></OwnerRoute>} />
      <Route path="/catalog" element={<OwnerRoute><Catalog /></OwnerRoute>} />
      <Route path="/vendors/:vendorId" element={<OwnerRoute><VendorDetail /></OwnerRoute>} />
      <Route path="/editors/:editorId" element={<OwnerRoute><EditorDetail /></OwnerRoute>} />
      <Route path="/delivery" element={<OwnerRoute><Delivery /></OwnerRoute>} />
      <Route path="/delivery/:projectId" element={<OwnerRoute><DeliveryDetail /></OwnerRoute>} />
      <Route path="/accounts" element={<OwnerRoute><Accounts /></OwnerRoute>} />
      <Route path="/settings" element={<OwnerRoute><Settings /></OwnerRoute>} />
      <Route path="/trash" element={<OwnerRoute><Trash /></OwnerRoute>} />
      <Route path="/settings/audit-log" element={<OwnerRoute><AuditLog /></OwnerRoute>} />
      <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
      <Route path="/today" element={<OwnerRoute><Today /></OwnerRoute>} />
      <Route path="/import" element={<OwnerRoute><Import /></OwnerRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        {/* Module 6C.4 Layer 1 — UpgradeModal listens for 402 events
            and pops the appropriate prompt. Mounted at App root so
            every protected route can trip it. */}
        <UpgradeModal />
        <Toaster
          position="bottom-right"
          duration={4000}
          toastOptions={{
            style: {
              background: "var(--brand-paper)",
              color: "var(--brand-ink)",
              border: "1px solid var(--brand-border)",
              borderRadius: 0,
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
